import React, { useCallback, useEffect, useMemo } from 'react';
import { Customer, SpecData, VehicleRec, ViewApptList, ascPageviewInput } from 'src/models';
import { strings } from 'src/localization';
import { IView } from 'Framework';
import { ComponentMapIds } from '../map';
import { ViewMode, CustMode } from 'src/enums';
import { Link } from '@framework-controls/core';
import { DateTime } from '@reynolds/datetime';
import css from './ApptSel.module.scss';
import ScrollBar from '../ScrollBar/ScrollBar';
import { ascPageview } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';


export interface IProps {
    customerInfo?: Customer;
    specInfo?: SpecData,
    clearMainPanes: () => void;
    pushToMainPanes: (inputView: IView) => void;
    setViewMode: (viewMode: ViewMode) => void;
    viewApptList?: ViewApptList;
    selectVehicle: (id: string) => void;
    setCustMode: (custMode: CustMode) => void;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    mainPaneViewOrder: IView[];
    setVehNextAppt: (vehicleIndex: string, apptID: string) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    locale:string;
    theme: string;
}

const ApptSel = (props: IProps) => {
    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Appt Sel', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.ApptSel) {
            const itemResults = props.viewApptList ? props.viewApptList?.length : 0;
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'itemlist',
                errorCode: '',
                itemResults: itemResults.toString(),
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.viewApptList, props.vehicles, props.selectedVehicle]);

    const shownList: any[] = [];

    const baseList = useMemo(() => {
        const baseList = [...props.viewApptList ?? []];

        const getTimeValue = (time) => (
            (Number(time.substring(0, 2)) * 60) +
            Number(time.substring(3, 5)) +
            (time.substring(5, 8) === 'pm' ? 720 : 0));

        // Sort the appt list by date/time
        baseList?.sort((a, b) => (
            Date.parse(a.date) > Date.parse(b.date) ||
            (Date.parse(a.date) === Date.parse(b.date) &&
                getTimeValue(a.time) > getTimeValue(b.time))
        ) ? 1 : -1);

        return baseList;
    }, [props?.viewApptList]);

    let selVeh = props.selectedVehicle;
    if (selVeh === 'AddVehicle') {
        selVeh = '';
    }

    const clickNumLinkHandler = useCallback((apptId: string) => {
        let vehicleForAppt = '';
        let vehicleForApptId = '';
        let vehMakeForAppt = '';
        let vehModelForAppt = '';
        let vehYearForAppt = '';
        baseList!.forEach((appt) => {
            if (appt.apptNum === apptId) {
                vehicleForAppt = appt.vin;
                vehMakeForAppt = appt.make;
                vehModelForAppt = appt.model;
                vehYearForAppt = appt.year;
            }
        });
        if (vehicleForAppt && vehicleForAppt !== '') {
            props.vehicles.forEach((vehicle) => {
                if (vehicleForAppt === vehicle.vin) {
                    vehicleForApptId = vehicle.id;
                }
            });
            props.selectVehicle(vehicleForApptId);
            props.setVehNextAppt(vehicleForApptId, apptId);
            props.pushToMainPanes({
                viewId: ComponentMapIds.VehLayout,
                component: ComponentMapIds.VehLayout,
            });
        }
        if (vehicleForAppt === '' || !vehicleForAppt) {
            props.vehicles.forEach((vehicle) => {
                if (vehMakeForAppt?.toUpperCase() === vehicle.make?.toUpperCase() &&
                vehModelForAppt?.toUpperCase() === vehicle.model?.toUpperCase() &&
                vehYearForAppt?.toUpperCase() === vehicle.year?.toUpperCase()) {
                    vehicleForApptId = vehicle.id;
                }
            });
            props.selectVehicle(vehicleForApptId);
            props.setVehNextAppt(vehicleForApptId, apptId);
            props.pushToMainPanes({
                viewId: ComponentMapIds.VehLayout,
                component: ComponentMapIds.VehLayout,
            });
        }
    }, [baseList, props.vehicles, props.selectVehicle, props.setVehNextAppt, props.pushToMainPanes]);


    const linkClassMap = {
        chevrolet: css.LinkChevrolet,
        bmw: css.LinkBMW,
    };

    const linkClass = linkClassMap[props.theme] || css.Link;

    // for loop of baseList to add div of baseList items plus hr above it.
    let tracker = 0;
    baseList?.forEach((appt, index) => {
        const dateValue = new DateTime(new Date(appt.date));
        // const MonthLit = dateValue.getMonthOfYear();
        // const displayDate = dateValue.format('DD, YYYY');
        // const fullDate = MonthLit + ' ' + displayDate;
        const fullDate = dateValue.toShortDateString();
        const timeValue = appt.time.substring(0, 5);
        let displayTime = timeValue + ' ' + appt.time.substring(5).toUpperCase();
        if (appt.transFlag === 'N') {
            displayTime = strings.DropOff + displayTime;
        }
        const apptVehYear = appt.year ?? '';
        let apptVehMake = appt.make ?? '';
        if (appt.makeCode === '2I'){
            apptVehMake = 'SCION';
        }
        const apptVehModel = appt.model ?? '';
        let vehVin: string | undefined = undefined;
        let vehMake: string | undefined = undefined;
        let vehYear: string | undefined = undefined;
        let vehModel: string | undefined = undefined;
        if (selVeh) {
            vehVin = props.vehicles.find(veh => veh.id === selVeh)?.vin;
            vehYear = props.vehicles.find(veh => veh.id === selVeh)?.year;
            vehMake = props.vehicles.find(veh => veh.id === selVeh)?.make;
            vehModel = props.vehicles.find(veh => veh.id === selVeh)?.model;
        }
        if (!selVeh || (!vehVin && vehYear === apptVehYear &&
            vehMake?.toUpperCase() === apptVehMake.toUpperCase() &&
            vehModel?.toUpperCase() === apptVehModel?.toUpperCase())) {
            shownList[index] = <div key={index}>
                <hr />
                <div className = {css.apptGrid}>
                    {/* <div className={css.apptVinDiv}>{appt.vin}</div> */}
                    <div className={css.apptVinDiv}>
                        {apptVehYear + ' ' + apptVehMake + ' ' + apptVehModel}
                    </div>
                    <div className={css.apptDateDiv}>{fullDate}</div>
                    <div className={css.apptTimeDiv}>{displayTime}</div>
                    <Link
                        className = {linkClass}
                        key={appt.apptNum}
                        id={appt.apptNum}
                        onClick={() => clickNumLinkHandler(appt.apptNum)}>
                        {appt.apptNum}
                    </Link>
                </div>
            </div>;
        } else if (vehVin) {
            if (appt.vin === vehVin) {
                shownList[tracker] = <div key={tracker}>
                    <hr />
                    <div className = {css.apptGrid}>
                        {/* <div className={css.apptVinDiv}>{appt.vin}</div> */}
                        <div className={css.apptVinDiv}>
                            {apptVehYear + ' ' + apptVehMake + ' ' + apptVehModel}
                        </div>
                        <div className={css.apptDateDiv}>{fullDate}</div>
                        <div className={css.apptTimeDiv}>{displayTime}</div>
                        <div className={css.apptNumDiv}>
                            <div className={css.linkDiv}>
                                <Link
                                    className = {linkClass}
                                    key={appt.apptNum}
                                    id={appt.apptNum}
                                    onClick={() => clickNumLinkHandler(appt.apptNum)}>
                                    {appt.apptNum}
                                </Link>
                            </div>
                            <div className={css.linkSpace}> </div>
                        </div>
                    </div>
                </div>;
                tracker = tracker + 1;
            }
        } else if (((vehModel === undefined && apptVehModel === '') &&
                    (vehYear === undefined && apptVehYear === '')) &&
                    vehMake?.toUpperCase() === apptVehMake.toUpperCase()) {
            shownList[index] = <div key={index}>
                <hr />
                <div className = {css.apptGrid}>
                    <div className={css.apptVinDiv}>
                        {apptVehYear + ' ' + apptVehMake + ' ' + apptVehModel}
                    </div>
                    <div className={css.apptDateDiv}>{fullDate}</div>
                    <div className={css.apptTimeDiv}>{displayTime}</div>
                    <Link
                        className = {linkClass}
                        key={appt.apptNum}
                        id={appt.apptNum}
                        onClick={() => clickNumLinkHandler(appt.apptNum)}>
                        {appt.apptNum}
                    </Link>
                </div>
            </div>;
        }
    });

    let time = strings.Time.substring(0, 4);
    if (props.locale === 'fr-CA') {
        time = strings.Time.substring(0, 5);
    }

    const columnHeadsStyles = classNames(
        css.columnHeads,
        {
            [css.columnHeadsFord]: props.theme === 'ford',
        },
    );


    return (
        <ScrollBar scrollVertical>
            <div className={css.mainDiv}>
                <h2 className={css.Name}>{props.customerInfo!.firstName + ','}</h2>
                <h3>{strings.MultApptSch}</h3>
                <hr />
                <h2>{strings.UpcomingAppt}</h2>
                <h3>{strings.ApptSelInfo}</h3>
                <hr />
                <div className={css.apptGrid}>
                    <div className={columnHeadsStyles}>{strings.Vehicle}</div>
                    <div className={columnHeadsStyles}>{strings.Date}</div>
                    <div className={columnHeadsStyles}>{time}</div>
                    <div className={columnHeadsStyles}>{strings.ApptNumber}</div>
                </div>
                {shownList}
                <hr />
            </div>
        </ScrollBar>

    );
};

export default ApptSel;